import type { Coordinate } from '@studio/js-utils/geometry';
import { toCoordinate } from '@studio/js-utils/geometry';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { PointerEvent, ReactElement } from 'react';
import { useState } from 'react';
import { Cursor } from '../cursor/index.js';
import type { HandlerProps } from '../types.js';
import { isPrimaryPointer } from '../utils.js';
import { Joystick } from './joystick.jsx';
import { drawCursor } from './utils.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import { locateProxy } from '#~/agent/utils.js';

/** `Tool.Joystick` `<Handler>` component. */
export const Handler = ({ deviceId, ...props }: HandlerProps): ReactElement => {
  const [engagement, setEngagement] = useState<{
    origin: Coordinate;
  }>();

  const { isProtected, triggerAlert: triggerControlAlert } =
    useAutoTrackingControlProtection(deviceId) ?? {};

  // Handle mouse events.
  const handlePointerEvent = useLatestFn((event: PointerEvent) => {
    if (!isPrimaryPointer(event)) {
      return;
    }

    // On `pointerdown` engage the joystick.
    if (event.type === 'pointerdown' && event.button === 0) {
      if (isProtected) {
        triggerControlAlert?.();

        return;
      }

      setEngagement({ origin: toCoordinate(event.clientX, event.clientY) });

      return;
    }

    // Otherwise disengage
    setEngagement(undefined);
  });

  const proxy = locateProxy(deviceId);

  const capabilities = proxy?.state.status?.device[deviceId]?.capabilities;

  if (
    !capabilities?.movement?.vectors?.pan?.enabled &&
    !capabilities?.movement?.vectors?.tilt?.enabled
  ) {
    return <></>;
  }

  return (
    <div onPointerDown={handlePointerEvent} onPointerUp={handlePointerEvent} {...props}>
      {engagement ? (
        <Joystick
          deviceId={deviceId}
          onPointerLeave={handlePointerEvent}
          origin={engagement.origin}
        />
      ) : (
        <Cursor onDrawCursor={drawCursor} />
      )}
    </div>
  );
};
